'use client';

import { useEffect, useState } from 'react';
import { Navigation } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  KEY_ROUTES,
  ROAD_COLOURS,
  ROAD_COLOUR_LABELS,
  type RoadColour,
  type RoadConditionsData,
  type RoadSection,
} from '@/lib/road-conditions';

const COLOUR_PRIORITY: RoadColour[] = ['green', 'yellow', 'amber', 'red', 'black'];

function worstColour(sections: RoadSection[]): RoadColour {
  let worst = 0;
  for (const s of sections) {
    const idx = COLOUR_PRIORITY.indexOf(s.colour);
    if (idx > worst) worst = idx;
  }
  return COLOUR_PRIORITY[worst];
}

export default function RouteCards({
  colourLabels,
}: {
  colourLabels?: Partial<Record<RoadColour, string>>;
} = {}) {
  const [data, setData] = useState<RoadConditionsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/road-conditions')
      .then((res) => res.json())
      .then((d: RoadConditionsData) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className={cn(
              'rounded-xl p-5 animate-pulse',
              'bg-white shadow-sm'
            )}
          >
            <div
              className={cn(
                'h-5 w-2/3 rounded',
                'bg-[#5A5A6E]/10'
              )}
            />
            <div
              className={cn(
                'h-4 w-1/2 rounded mt-3',
                'bg-[#5A5A6E]/10'
              )}
            />
            <div
              className={cn(
                'h-4 w-1/3 rounded mt-2',
                'bg-[#5A5A6E]/10'
              )}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {KEY_ROUTES.slice(0, 4).map((route) => {
        const matchingSections = data
          ? data.roadSections.filter((s) => route.roadNumbers.includes(s.roadNumber))
          : [];
        const colour = matchingSections.length > 0 ? worstColour(matchingSections) : 'green';

        return (
          <div
            key={route.id}
            className={cn(
              'rounded-xl p-5 transition-colors',
              'bg-white shadow-sm'
            )}
          >
            <div className="flex items-center gap-2 mb-2">
              <Navigation
                className="h-4 w-4 shrink-0 text-[#9B7B3A]"
              />
              <h3
                className={cn(
                  'font-light text-base',
                  'text-[#1A1A2E]'
                )}
              >
                {route.name}
              </h3>
            </div>

            <p
              className={cn(
                'text-sm mb-3',
                'text-[#5A5A6E]'
              )}
            >
              {route.distance} &middot; {route.time}
            </p>

            <div className="flex items-center gap-2">
              <span
                className="inline-block h-3 w-3 rounded-full shrink-0"
                style={{ backgroundColor: ROAD_COLOURS[colour] }}
              />
              <span
                className={cn(
                  'text-sm font-medium',
                  'text-[#1A1A2E]'
                )}
              >
                {colourLabels?.[colour] ?? ROAD_COLOUR_LABELS[colour]}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
